import { useState, useMemo, useEffect } from "react";
import { Box, Typography, IconButton, Avatar, Divider, Stack, Autocomplete, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import dayjs from "dayjs";
import { StyledTextField, NormalButton } from "../../../components/ui/CommonReusables";
import { primaryButtonSx, subtleIconSx, autocompleteSx } from "./buttonStyles";

const initialsOf = (name) =>
	name
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0])
		.join("")
		.toUpperCase();

/**
 * Picker plus roster for a single training. Agents marked here are the ones that earn the
 * training badge. Shared by the per-curriculum accordion and the calendar's training dialog so
 * both edit attendance the same way.
 *
 * `roster` is the live agent list from the server — the pool this training can draw from.
 */
const AttendanceEditor = ({ training, roster = [], onMarkAttended, onRemoveAttendance, maxListHeight = 260 }) => {
	const [selected, setSelected] = useState([]);

	// Only agents who have not been marked for this training can be added.
	const available = useMemo(() => {
		const attendedIds = new Set(training.attendees.map((attendee) => attendee.id));
		return roster.filter((agent) => !attendedIds.has(agent.id));
	}, [training.attendees, roster]);

	// Marking is confirmed in a prompt further up, so picks are held until they actually land —
	// clearing on click would empty the picker on a cancelled or failed confirmation. Only the
	// agents that made it through are dropped, so a partially applied batch keeps the rest.
	useEffect(() => {
		const attendedIds = new Set(training.attendees.map((attendee) => attendee.id));

		setSelected((prev) => {
			const remaining = prev.filter((agent) => !attendedIds.has(agent.id));
			return remaining.length === prev.length ? prev : remaining;
		});
	}, [training.attendees]);

	const handleMark = () => {
		if (selected.length === 0) return;

		onMarkAttended(selected);
	};

	return (
		<Box sx={{ width: "100%" }}>
			{/* Each child is given an explicit width — the surrounding theme neutralizes the flex stretch. */}
			<Stack
				direction={{ xs: "column", sm: "row" }}
				spacing={1.5}
				alignItems={{ xs: "stretch", sm: "flex-start" }}
				sx={{ width: "100%", "& > *": { maxWidth: "100%" } }}
			>
				<Autocomplete
					multiple
					disableCloseOnSelect
					fullWidth
					size="small"
					options={available}
					value={selected}
					onChange={(_event, value) => setSelected(value)}
					getOptionLabel={(option) => option.name}
					isOptionEqualToValue={(option, value) => option.id === value.id}
					noOptionsText="Every agent is already marked"
					sx={autocompleteSx}
					renderInput={(params) => (
						<StyledTextField {...params} placeholder={selected.length === 0 ? "Select agents..." : ""} />
					)}
				/>

				<NormalButton
					startIcon={<AddIcon />}
					onClick={handleMark}
					disabled={selected.length === 0}
					sx={{ flexShrink: 0, ...primaryButtonSx }}
				>
					{selected.length > 1 ? `Mark ${selected.length} Attended` : "Mark Attended"}
				</NormalButton>
			</Stack>

			<Box sx={{ mt: 2, width: "100%", maxHeight: maxListHeight, overflowY: "auto" }}>
				{training.attendees.length === 0 ? (
					<Box sx={{ py: 3, textAlign: "center" }}>
						<Typography sx={{ color: "text.secondary", fontSize: "0.8rem" }}>No agents marked as attended yet</Typography>
					</Box>
				) : (
					training.attendees.map((attendee, index) => (
						<Box key={attendee.id}>
							<Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 1 }}>
								<Avatar sx={{ width: 32, height: 32, bgcolor: "#ECFFF7", color: "#0F7A53", fontWeight: 700, fontSize: 11 }}>
									{initialsOf(attendee.name)}
								</Avatar>

								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Typography sx={{ fontWeight: 600, fontSize: "0.825rem" }} noWrap>
										{attendee.name}
									</Typography>
									<Typography sx={{ color: "text.secondary", fontSize: "0.7rem" }} noWrap component="div">
										Attended {dayjs(attendee.attended_at).format("MMM D, YYYY")}
									</Typography>
								</Box>

								<Tooltip title="Remove attendance">
									<IconButton
										size="small"
										onClick={() => onRemoveAttendance(attendee.id)}
										aria-label={`Remove ${attendee.name} from ${training.title}`}
										sx={subtleIconSx}
									>
										<RemoveCircleOutlineIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</Stack>

							{index < training.attendees.length - 1 && <Divider />}
						</Box>
					))
				)}
			</Box>
		</Box>
	);
};

export default AttendanceEditor;
